import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { Plus as PlusIcon, WarningHexagon } from 'iconoir-react'
import { isEmpty, isEqual } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { z } from 'zod'
import { useMutation } from 'urql'
import { Button } from 'app/components/ui/button'
import { DialogActionBar } from 'app/components/ui/dialog'
import { useConfirmation } from '../ConfirmationContext'
import FormCombo from '../form/FormCombo'
import FormInput from '../form/FormInput'
import { SmartForm, SubAction } from '../form/SmartForm'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { ChannelMappingType, defaultChannelMappings, defaultChannels } from 'lib/channelDefaults'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const channelMappingValidator = withZod(
  z.object({
    source: z.string().min(1, { message: `Source is required` }),
    medium: z.string().min(1, { message: `Medium is required` }),
    channel: z.string().min(1, { message: `Channel is required` }),
  }),
)

const CREATE_CHANNEL_MAPPING = graphql(`
  mutation CreateChannelMapping($input: CreateChannelMappingInput!) {
    createChannelMapping(input: $input) {
      id
      source
      medium
      channel
      orgId
      workspaceId
    }
  }
`)

const UPDATE_CHANNEL_MAPPING = graphql(`
  mutation UpdateChannelMapping($id: ID!, $input: UpdateChannelMappingInput!) {
    updateChannelMapping(id: $id, input: $input) {
      id
      source
      medium
      channel
      orgId
      workspaceId
    }
  }
`)

const DELETE_CHANNEL_MAPPING = graphql(`
  mutation DeleteChannelMapping($id: ID!) {
    deleteChannelMapping(id: $id)
  }
`)

export function ChannelMappingForm({
  channel,
  isDefaultChannel,
  onSubmit,
}: {
  channel?: ChannelMappingType
  isDefaultChannel: boolean
  onSubmit?: (data: { channelMapping: Partial<ChannelMappingType> }) => void
}): ReactElement {
  const [confirm] = useConfirmation()
  const workspace = useWorkspace()
  const [createResult, executeCreate] = useMutation(CREATE_CHANNEL_MAPPING)
  const [updateResult, executeUpdate] = useMutation(UPDATE_CHANNEL_MAPPING)
  const [deleteResult, executeDelete] = useMutation(DELETE_CHANNEL_MAPPING)
  const isLoading = createResult.fetching || updateResult.fetching || deleteResult.fetching
  const [subActionState, setSubActionState] = useState<SubAction | undefined>()
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false)
  const [selectedChannel, setSelectedChannel] = useState<OptionType | undefined>(
    channel?.channel ? { value: channel.channel, id: channel.id!, name: channel.channel } : undefined,
  )

  const handleSubmit = async (data: Partial<ChannelMappingType>, subAction: SubAction): Promise<void> => {
    setIsDuplicate(false)
    const result = await channelMappingValidator.validate(data)
    if (workspace == null) {
      logError(`workspace not found`)
      notifyError(`Please select a workspace`)
      return
    }
    if (result.error) {
      logError(`error saving channel mapping`, result.error)
      notifyError(`Error Saving`)
      return
    }
    if (
      defaultChannelMappings.find(([souce, mmedium, channel]) =>
        isEqual([data.source, data.medium, channel], [souce, mmedium, channel]),
      )
    ) {
      setIsDuplicate(true)
      return
    }
    if (subAction === SubAction.CREATE) {
      const { data, error } = await executeCreate({
        input: { source: result.data.source, medium: result.data.medium, channel: result.data.channel },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`Channel Mapping Created`)
      onSubmit?.({ channelMapping: data!.createChannelMapping })
      closeModal()
    } else if (subAction === SubAction.EDIT) {
      const { data, error } = await executeUpdate({
        id: channel!.id!,
        input: { source: result.data.source, medium: result.data.medium, channel: result.data.channel },
      })
      if (error) {
        setIsDuplicate(true)
        return
      }
      notifySuccess(`Change Saved. This may take some time to fully apply.`)
      onSubmit?.({ channelMapping: data!.updateChannelMapping })
      closeModal()
    }
  }

  const handleDelete = async (id: string) => {
    if (isDefaultChannel) return
    const { error } = await executeDelete({ id }, { additionalTypenames: ['ChannelMapping'] })
    if (error) {
      notifyError(`Error Deleting`)
      return
    }
    notifySuccess(`Deleted Successfully`)
    closeModal()
  }

  useEffect(() => {
    if (isEmpty(channel) || channel?.id == null) {
      setSubActionState(SubAction.CREATE)
    } else setSubActionState(SubAction.EDIT)
  }, [channel])

  return (
    <SmartForm<Partial<ChannelMappingType>>
      id="channel-mapping-form"
      validator={channelMappingValidator}
      className="mb-0 mt-0 flex w-full flex-col gap-y-0"
      defaultValues={channel}
      onSubmit={(data) => handleSubmit(data, isEmpty(channel) || data.id == null ? SubAction.CREATE : SubAction.EDIT)}
      hiddenInputs={[{ name: `id`, value: channel?.id }]}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col px-6 py-4">
        <div className="flex items-start gap-x-2">
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 w-44 !px-2 !py-1"
            name="source"
            type="text"
            label="Source"
            cols={3}
          />
          <PlusIcon className="size-5 mt-9" strokeWidth={1.5} />
          <FormInput
            labelClassName="text-strong w-44 text-body-sm font-medium"
            className="h-10 !px-2 !py-1"
            name="medium"
            type="text"
            label="Medium"
            cols={3}
          />
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke="currentColor"
            className="size-5 mt-9"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.499 8.248h15m-15 7.501h15" />
          </svg>
          <FormCombo
            label="Channel"
            labelClassName="text-strong w-44 text-body-sm font-medium"
            className="w-44"
            inputClassName="!h-10 !px-2 !py-1 text-body-sm font-medium"
            name="channel"
            placeholder="Select Channel"
            value={selectedChannel}
            options={defaultChannels.map((channel) => ({
              id: channel,
              value: channel,
              name: channel,
            }))}
            onChange={setSelectedChannel}
            cols={3}
          />
        </div>
      </div>
      <DialogActionBar
        className="col-span-6"
        start={
          <>
            {subActionState === SubAction.EDIT && (
              <Button
                type="button"
                variant="danger"
                size="md"
                loading={isLoading}
                onClick={async () => {
                  await confirm({
                    title: `Delete channel mapping`,
                    description: `Are you sure you want to delete the channel mapping`,
                    variant: `danger`,
                  }).then(async () => handleDelete(channel?.id!))
                }}
              >
                Delete
              </Button>
            )}
            {isDuplicate && (
              <div className="inline-flex items-center gap-x-2 text-red-700">
                <WarningHexagon className="size-4" />
                <span className="text-body-xs">This Mapping Already Exists</span>
              </div>
            )}
          </>
        }
      >
        <Button type="button" variant="secondary" size="md" onClick={() => closeModal()} disabled={isLoading}>
          Cancel
        </Button>
        {subActionState && (
          <Button type="submit" variant="primary" size="md" loading={isLoading}>
            {subActionState === SubAction.CREATE ? `Create` : `Update`}
          </Button>
        )}
      </DialogActionBar>
    </SmartForm>
  )
}
